import type { Phase, Round } from "@/types"

/**
 * Identifies one "everybody finish this, then we move on" gate.
 *
 * A participant records WHICH gate they finished rather than a plain `done`
 * boolean, because a boolean cannot express the flow:
 *
 *   - Two gates live in the same round. The Pains round asks the room to finish
 *     twice — once writing, once voting — so resetting on a round change is too
 *     coarse to separate them.
 *   - Gates repeat per job. Picking cards happens again for every job the room
 *     works, so job two's pick must not inherit job one's finishers.
 *   - Any reset is a race. Clearing a flag for everyone is one write per
 *     participant, none of them atomic with the phase change, so a participant
 *     who finishes the new gate quickly would have it wiped by a reset still in
 *     flight.
 *
 * Keying by (job, round, phase) removes all three: nothing is ever reset,
 * `donePhase` simply stops matching where the room now is. A participant who
 * finished a gate the facilitator has already left keeps a key that no longer
 * matches, which is exactly right — they finished that gate, not this one.
 */
export function donePhaseKey(jobId: string | null, round: Round, phase: Phase): string {
  // Rounds before the first job (lobby, pains) have no job to scope to.
  return `${jobId ?? "-"}:${round}:${phase}`
}
